import { useCallback } from 'react';
import { Thread as UnifiedThread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { Options } from '@frontend/fetch';
import { useSchemaQueryUpdaters } from '@frontend/react-query-helpers';
import { isSmsMessageItem } from '../../../utils/thread-items-utils';
import { useFetchGetUnifiedThreadDetails } from '../../queries/use-get-unified-thread-details';
import { serviceName } from '../../service';
import { ServiceQueries } from '../../types';
import { ResourceMatchingOptions } from '../types';
import { findCachedUnifiedThreadInListUnifiedThreads } from './cache-resource-getters';

/**
 * Returns `undefined` if the unified thread is not valid.
 * For unified threads, we validate based on ThreadItems instead of direct messages array.
 */
const validateUnifiedThread = (thread?: UnifiedThread): UnifiedThread | undefined => {
  const firstSmsItem = thread?.items?.find(isSmsMessageItem);
  const firstSms = firstSmsItem?.smsMessage;

  const isValidThread =
    !!firstSms &&
    !!firstSms.createdAt &&
    !!firstSms.id &&
    (!!firstSms.body || !!firstSms.numMedia || !!firstSms.media?.length);
  return isValidThread ? thread : undefined;
};

type UseGetUnifiedThreadDetailsArgs = {
  httpOptions?: Options;
};

export const useGetUnifiedThreadDetails = ({ httpOptions }: UseGetUnifiedThreadDetailsArgs = {}) => {
  const queryUpdaters = useSchemaQueryUpdaters<ServiceQueries>(serviceName);
  const fetchGetUnifiedThreadDetails = useFetchGetUnifiedThreadDetails();

  const getUnifiedThreadDetails = useCallback(
    async ({
      threadId,
      locationId,
      excludeLastAutomated,
      cacheOnly = false,
      matchStrategy,
      matchFn,
      httpOptions: customHttpOptions,
    }: {
      threadId: string;
      locationId: string;
      excludeLastAutomated: boolean;
      cacheOnly?: boolean;
      httpOptions?: Options;
    } & Partial<Pick<ResourceMatchingOptions<UnifiedThread>, 'matchStrategy' | 'matchFn'>>) => {
      const listUnifiedThreadsCachesToSearch = queryUpdaters.getQueriesData<'ListUnifiedThreads', true>({
        endpointName: 'ListUnifiedThreads',
      });

      let cachedThreadFromListUnifiedThreads: UnifiedThread | undefined;
      if (excludeLastAutomated) {
        // If we are excluding the last automated message one of the following must be true:
        // - Find a matching thread on a request with the same filter
        // - Find a matching thread with a manual message
        const { matchingFilterQueries, otherQueries } = listUnifiedThreadsCachesToSearch.reduce<{
          matchingFilterQueries: (typeof listUnifiedThreadsCachesToSearch)[number][1][];
          otherQueries: (typeof listUnifiedThreadsCachesToSearch)[number][1][];
        }>(
          (acc, [queryKey, data]) => {
            const requestMatchesAutomatedMessageFilter =
              !!queryKey[2].filter?.excludeAutomatedLastMessage === !!excludeLastAutomated;
            if (requestMatchesAutomatedMessageFilter) {
              acc.matchingFilterQueries.push(data);
            } else {
              acc.otherQueries.push(data);
            }
            return acc;
          },
          { matchingFilterQueries: [], otherQueries: [] }
        );

        cachedThreadFromListUnifiedThreads =
          findCachedUnifiedThreadInListUnifiedThreads(matchingFilterQueries, {
            matchValues: { threadId, locationId },
            matchStrategy: matchStrategy ?? 'or',
            matchFn,
          }) ??
          findCachedUnifiedThreadInListUnifiedThreads(otherQueries, {
            matchValues: { threadId, locationId },
            matchStrategy: 'and',
            matchFn: (thread) => {
              // For unified threads, check if first SMS item is manual
              const firstSmsItem = thread.items?.find(isSmsMessageItem);
              const messageIsManual = !firstSmsItem?.smsMessage?.autogeneratedBy;
              return messageIsManual && (matchFn?.(thread) ?? true);
            },
          });
      } else {
        cachedThreadFromListUnifiedThreads = findCachedUnifiedThreadInListUnifiedThreads(
          listUnifiedThreadsCachesToSearch.map(([, data]) => data),
          { matchValues: { threadId, locationId }, matchStrategy: 'or' }
        );
      }

      if (cachedThreadFromListUnifiedThreads) {
        return validateUnifiedThread(cachedThreadFromListUnifiedThreads);
      }

      if (cacheOnly) {
        const cachedUnifiedThreadDetails = queryUpdaters.getQueriesData({
          endpointName: 'GetUnifiedThreadDetails',
          queryFilters: {
            queryKey: queryUpdaters.getQueryKey<'GetUnifiedThreadDetails'>({
              endpointName: 'GetUnifiedThreadDetails',
              request: {
                threadId,
                locationId,
                excludeAutomatedLastMessage: excludeLastAutomated ? true : undefined,
              },
            }),
          },
        });
        const thread = cachedUnifiedThreadDetails[0]?.[1]?.thread;
        if (matchStrategy === 'and' && matchFn && thread && !matchFn(thread)) return undefined;
        return validateUnifiedThread(thread);
      } else {
        try {
          const result =
            threadId && locationId
              ? await fetchGetUnifiedThreadDetails({
                  request: {
                    threadId,
                    locationId,
                    excludeAutomatedLastMessage: excludeLastAutomated ? true : undefined,
                  },
                  options: {
                    retryDelay: 500,
                    retry: 2,
                    cacheTime: 0,
                    staleTime: 0,
                  },
                  httpOptions: { ...httpOptions, ...customHttpOptions },
                })
              : undefined;

          return result?.thread?.id ? validateUnifiedThread(result.thread) : undefined;
        } catch (error) {
          return undefined;
        }
      }
    },
    [fetchGetUnifiedThreadDetails, httpOptions, queryUpdaters]
  );

  return getUnifiedThreadDetails;
};
